// External libraries
import { useEffect, useRef, useState } from "react";

// FontAwesome icons
import {
  faChevronLeft,
  faChevronRight,
  faCog,
  faDatabase,
  faEllipsis,
  faEye,
  faList,
  faRectanglesMixed,
  faSquarePlus,
  faUpDown,
  faWindowFlip,
  faXmark,
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Custom Components
import { CustomPeoplePicker } from "../Common/UI/CustomPeoplePicker";

// Preact Signals
import { batch, computed, signal } from "@preact/signals-react";

// Kendo React components
import { Button, Chip } from "@progress/kendo-react-buttons";
import { Icon } from "@progress/kendo-react-common";
import { Window } from "@progress/kendo-react-dialogs";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import { Badge, BadgeContainer } from "@progress/kendo-react-indicators";
import {
  Checkbox,
  InputClearValue,
  InputPrefix,
  InputSeparator,
  RadioGroup,
  Switch,
  TextBox,
} from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { Menu, MenuItem } from "@progress/kendo-react-layout";
import { ListView } from "@progress/kendo-react-listview";
import { Popup } from "@progress/kendo-react-popup";
import { Sortable } from "@progress/kendo-react-sortable";
import { Popover } from "@progress/kendo-react-tooltip";
import {
  moveTreeViewItem,
  TreeView,
  TreeViewDragAnalyzer,
  TreeViewDragClue,
} from "@progress/kendo-react-treeview";

// Internal components
import PersonWithSuspense from "../Common/MicrosoftGraph/MgtPerson";
import ColorChangingIcon from "../Common/UI/ColorChangingIcon";
import { fieldIconMap } from "../Fields/FieldTypes/FieldIconMap";
import { Section } from "../Fields/FieldTypes/Sections";
import ConfirmAllowFieldNameEditDialog from "../Notification/ConfirmAllowFieldNameEditDialog";
import { DataConnectionConfigurator } from "./DataConnectionConfigurator/DataConnectionConfigurator";

// Utilities
import { getHighlight } from "../Utilities/CommonUtils";

// Signals
import {
  allDatasets,
  conditionalRowFormattingVisibility,
  isAdmin,
  userData,
  userTemplates,
  visibilityBuilderVisibility,
} from "../Common/Store/CommonSignals";
import { userBelongsToUserGroup } from "../Utilities/AuthorizationUtil";
import {
  configureDataOffset,
  expandedHierarchyItems,
  expandedPermissionItems,
  filteredDataConnections,
  isStageEditor,
  selectedDataConnectionItem,
  selectedTemplateMenuItem,
  showConfigureDataPopover,
  showConfirmFieldNameEditDialog,
  showDataConnectionConfigurator,
  stageStepsMaxID,
  templateData,
  templateInformationPaneVisible,
  templateInformationSearch,
  templatePermissionTreeView,
  templateTreeView,
} from "./NewTemplateSignals";

const selectedTreeItem = signal();
const selectedPerson = signal();
const selectedPeopleFromGroup = signal([]);
const isGroupSelection = signal(false);
const selectedGroupData = signal(null);
const selectedPermission = signal("");
const existingPermissionErrorMessage = signal(null);
const showContextMenu = signal(false);
const contextMenuPosition = signal({ left: 0, top: 0 });
const isDragging = signal(false);
const isHovering = signal(false);
const treeViewCursor = computed(() => {
  if (!isHovering.value) return "default";
  return isDragging.value ? "grabbing" : "grab";
});

const PaneHeader = computed(() => {
  return (
    <div className="!k-w-full">
      <DropDownList
        className="!k-justify-content-stretch"
        data={["Data Connections", "Tree View", "Permissions", "Template Properties"]}
        value={selectedTemplateMenuItem.value}
        onChange={(e) => (selectedTemplateMenuItem.value = e.target.value)}
      />
    </div>
  );
});

const SortableItemUI = (props) => {
  const { style, attributes, dataItem, forwardRef } = props;
  return (
    <div
      className="k-d-flex k-align-items-center k-fs-lg k-p-1 k-rounded-md k-cursor-move k-mb-1 k-mt-1 k-border-toolbar k-border-solid k-border-1 k-user-select-none k-justify-content-between"
      ref={forwardRef}
      {...attributes}
      style={{
        ...style,
      }}
    >
      <div>
        <FontAwesomeIcon
          icon={faUpDown}
          className="k-mr-4 k-ml-2"
        />
        {dataItem.text}
      </div>
      <ColorChangingIcon
        className="k-mr-2"
        icon={faXmark}
        initialPrimaryColor="#D6562A"
        onClick={() => {
          const newTemplateData = { ...templateData.value };
          newTemplateData.sections.forEach((section) => {
            if (section.properties?.StageStep?.id === dataItem.id) {
              delete section.properties.StageStep;
            }
          });
          newTemplateData.properties.StageSteps = newTemplateData.properties.StageSteps.filter(
            (step) => step.id !== dataItem.id
          );
          templateData.value = newTemplateData;
        }}
      />
    </div>
  );
};

const expressionPrefix = () => {
  return (
    <>
      <InputPrefix>
        <ColorChangingIcon
          icon={faRectanglesMixed}
          initialPrimaryColor="#605e5c"
          initialSecondaryColor="#0078D4"
          margin="1px 5px 0 5px"
          onClick={(e) => {
            e.stopPropagation();
            conditionalRowFormattingVisibility.value = true;
          }}
        />
      </InputPrefix>
      <InputSeparator
        style={{
          width: "1px",
          backgroundColor: "gainsboro",
          margin: "4px 0px",
        }}
      />
    </>
  );
};

const expressionSuffix = (handlePropertyChange) => {
  return (
    <>
      <InputSeparator
        style={{
          width: "1px",
          backgroundColor: "gainsboro",
          margin: "4px 0px",
        }}
      />
      <InputClearValue
        onClick={() =>
          handlePropertyChange({
            propName: "ConditionalRowFormatting",
            propValue: [],
          })
        }
      >
        <Icon name="x" />
      </InputClearValue>
    </>
  );
};

const stagesPrefix = (addStageItem) => {
  return (
    <>
      <ColorChangingIcon
        margin="4px 5px 0px 4px"
        initialPrimaryColor="#a6e6ff"
        initialSecondaryColor="#0078d4"
        icon={faSquarePlus}
        onClick={addStageItem}
      />
      <InputSeparator
        style={{
          width: "1px",
          backgroundColor: "gainsboro",
          margin: "4px 0px",
        }}
      />
    </>
  );
};

export const TemplateInformationPane = () => {
  const anchor = useRef();

  useEffect(() => {
    document.addEventListener("click", () => {
      if (showContextMenu.value) {
        showContextMenu.value = false;
      }
    });
  }, []);

  // Clear selection and error message when template changes or component unmounts
  useEffect(() => {
    return () => {
      clearPermissionSelection();
      existingPermissionErrorMessage.value = null;
    };
  }, [templateData.value?.id]);

  // Helper function to check if a user belongs to any group in permissions
  const userBelongsToGroup = (email, groupsInPermissions) => {
    return groupsInPermissions.some((groupPerm) => {
      const groupDataset = allDatasets.value?.find(
        (ds) => ds.datasetType === "User Group" && ds.id === groupPerm.userGroupId
      );

      if (!groupDataset) return false;

      const groupMembers =
        groupDataset.values
          ?.map((v) => {
            try {
              const person = typeof v.value === "string" ? JSON.parse(v.value) : v.person;
              return person?.email?.toLowerCase();
            } catch {
              return null;
            }
          })
          .filter(Boolean) || [];

      return groupMembers.includes(email.toLowerCase());
    });
  };

  const clearPermissionSelection = () => {
    selectedPerson.value = undefined;
    selectedPeopleFromGroup.value = [];
    isGroupSelection.value = false;
    selectedGroupData.value = null;
    selectedPermission.value = "";
  };

  const groupExistsInPermissions = (groupId) => {
    const existsInCoOwners = templateData.value.coOwners.some(
      (co) => co.isUserGroup && co.userGroupId === groupId
    );
    const existsInShared = templateData.value.sharedPermission.some(
      (sp) => sp.isUserGroup && sp.userGroupId === groupId
    );
    return existsInCoOwners || existsInShared;
  };

  const userExistsInPermissions = (email) => {
    const lowerEmail = email.toLowerCase();
    const existsInCoOwners = templateData.value.coOwners.some(
      (co) => !co.isUserGroup && co.user.toLowerCase() === lowerEmail
    );
    const existsInShared = templateData.value.sharedPermission.some(
      (sp) => !sp.isUserGroup && sp.entity.toLowerCase() === lowerEmail
    );
    return existsInCoOwners || existsInShared;
  };

  const removeGroupMembersFromPermissions = (memberEmails) => {
    const updatedCoOwners = templateData.value.coOwners.filter(
      (co) => co.isUserGroup || !memberEmails.includes(co.user.toLowerCase())
    );
    const updatedSharedPermission = templateData.value.sharedPermission.filter(
      (sp) => sp.isUserGroup || !memberEmails.includes(sp.entity.toLowerCase())
    );
    return { updatedCoOwners, updatedSharedPermission };
  };

  const getGroupMemberEmails = (members) =>
    members.map((member) => member.email?.toLowerCase()).filter(Boolean);

  const handleGroupPermissionSubmit = () => {
    const groupEntity = selectedGroupData.value.displayName;
    const groupId = selectedGroupData.value.groupId;
    const groupMembers = selectedGroupData.value.members || [];

    if (groupExistsInPermissions(groupId)) {
      existingPermissionErrorMessage.value = "The group has already been added to the permissions";
      return;
    }

    const memberEmails = getGroupMemberEmails(groupMembers);
    const { updatedCoOwners, updatedSharedPermission } =
      removeGroupMembersFromPermissions(memberEmails);

    if (selectedPermission.value === "Co-Owner") {
      templateData.value = {
        ...templateData.value,
        coOwners: [
          ...updatedCoOwners,
          { user: groupEntity, isUserGroup: true, userGroupId: groupId },
        ],
        sharedPermission: updatedSharedPermission,
      };
    } else {
      templateData.value = {
        ...templateData.value,
        coOwners: updatedCoOwners,
        sharedPermission: [
          ...updatedSharedPermission,
          {
            entity: groupEntity,
            permission: selectedPermission.value,
            isUserGroup: true,
            userGroupId: groupId,
          },
        ],
      };
    }
    existingPermissionErrorMessage.value = null;
    clearPermissionSelection();
  };

  const handleUserPermissionSubmit = () => {
    const allGroupsInPermissions = [
      ...templateData.value.coOwners.filter((co) => co.isUserGroup),
      ...templateData.value.sharedPermission.filter((sp) => sp.isUserGroup),
    ];

    const email = (selectedPerson.value?.email || "").toLowerCase();

    if (userExistsInPermissions(email)) {
      existingPermissionErrorMessage.value = "The user has already been added to the permissions";
      return;
    }

    if (userBelongsToGroup(email, allGroupsInPermissions)) {
      existingPermissionErrorMessage.value = "The user belongs to a group already in permissions";
      return;
    }

    if (selectedPermission.value === "Co-Owner") {
      templateData.value = {
        ...templateData.value,
        coOwners: [
          ...templateData.value.coOwners,
          { user: selectedPerson.value.email, isUserGroup: false },
        ],
      };
    } else {
      templateData.value = {
        ...templateData.value,
        sharedPermission: [
          ...templateData.value.sharedPermission,
          {
            entity: selectedPerson.value.email,
            permission: selectedPermission.value,
            isUserGroup: false,
          },
        ],
      };
    }
    existingPermissionErrorMessage.value = null;
    clearPermissionSelection();
  };

  const handlePermissionSubmit = () => {
    if (isGroupSelection.value && selectedGroupData.value) {
      handleGroupPermissionSubmit();
    } else {
      handleUserPermissionSubmit();
    }
  };

  const onSharedPermissionExpandChange = (event) => {
    expandedPermissionItems.value = {
      ...expandedPermissionItems.value,
      [event.item.text]: !expandedPermissionItems.value[event.item.text],
    };
  };

  const listViewItem = ({ dataItem }) => {
    if (!dataItem?.type) return;

    let icon = "";
    switch (dataItem.type) {
      case "Template":
        icon = faWindowFlip;
        break;
      case "List":
        icon = faList;
        break;
      case "Global Parameters":
        icon = faCog;
        break;
      default:
        icon = faDatabase;
        break;
    }

    return (
      <div className="k-d-flex k-justify-content-between k-mt-2 k-mb-2">
        <div className="k-d-flex">
          <div className="k-vertical-align-center">
            <FontAwesomeIcon
              className="k-mr-2 k-ml-2"
              style={{
                "--fa-primary-color": "#0078d4",
                "--fa-secondary-color": "#a6e6ff",
                "--fa-secondary-opacity": 1,
              }}
              icon={icon}
              fontSize={32}
            />
          </div>
          <div className="k-vertical-align-center">
            <div>{getHighlight(dataItem.text, templateInformationSearch.value)}</div>
            <div style={{ fontSize: ".55rem" }}>{dataItem.id}</div>
          </div>
        </div>
        <div className="k-vertical-align-center">
          <ColorChangingIcon
            className="k-justify-content-end k-mr-2"
            name="btnConfigureDataConnection"
            title="Configure Connection"
            icon={faEllipsis}
            initialPrimaryColor="#0078d4"
            initialSecondaryColor="#0078d4"
            initialSize={28}
            hoverSize={28}
            ref={anchor}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              batch(() => {
                configureDataOffset.value = { top: e.clientY + 10, left: e.clientX };
                showConfigureDataPopover.value = !showConfigureDataPopover.value;
                selectedDataConnectionItem.value = dataItem;
              });
            }}
          />
        </div>
      </div>
    );
  };

  const templateHierarchyItem = ({ e }) => {
    const fieldType = e.item.type;
    const fieldIcon = fieldIconMap[fieldType] || Section.icon;

    return (
      <div className="k-d-flex k-vertical-align-center">
        {fieldIcon()}
        {getHighlight(e.item.text, templateInformationSearch.value)}
      </div>
    );
  };

  const templatePermissionItem = ({ e }) => {
    if (!e.item.user) return <div>{e.item.text}</div>;
    return (
      <div>
        <div className="k-d-flex">
          {e.itemHierarchicalIndex !== "0_0" && (
            <div className="k-vertical-align-center">
              <ColorChangingIcon
                className="k-mr-2"
                icon={faXmark}
                initialPrimaryColor="#D6562A"
                onClick={() => {
                  if (e.itemHierarchicalIndex.includes("1_")) {
                    templateData.value = {
                      ...templateData.value,
                      coOwners: templateData.value.coOwners.filter(
                        (coOwner) => coOwner.user !== e.item.user
                      ),
                    };
                  } else {
                    templateData.value = {
                      ...templateData.value,
                      sharedPermission: templateData.value.sharedPermission.filter(
                        (permission) => permission.entity !== e.item.user
                      ),
                    };
                  }
                }}
                hidden={
                  !isAdmin.value &&
                  templateData.value?.owner.toLowerCase() !==
                    userData.value?.userEmail.toLowerCase() &&
                  !templateData.value?.coOwners.some((co) => {
                    // Check individual co-owners
                    if (
                      !co.isUserGroup &&
                      co.user.toLowerCase() === userData.value?.userEmail.toLowerCase()
                    ) {
                      return true;
                    }
                    // Check if user belongs to a co-owner group
                    if (co.isUserGroup && co.userGroupId) {
                      return userBelongsToUserGroup(userData.value?.userEmail, co.userGroupId);
                    }
                    return false;
                  })
                }
              />
            </div>
          )}
          {e.item.isUserGroup ? (
            <div className="k-d-flex k-align-items-center">
              <span style={{ marginRight: "8px" }}>📁</span>
              <div>
                <div>{e.item.user} (User Group)</div>
                {e.item.permission && (
                  <div style={{ fontSize: "0.8em", color: "#666" }}>{e.item.permission}</div>
                )}
              </div>
            </div>
          ) : (
            <PersonWithSuspense
              email={e.item.user}
              view={e.itemHierarchicalIndex.includes("2_") ? "twolines" : "oneline"}
              line2Property={e.item.permission}
              avatarType="initials"
            />
          )}
        </div>
      </div>
    );
  };

  //Begin Conditional Formatting
  const [conditionalRowFormatting, setConditionalRowFormatting] = useState(
    templateData.value?.properties?.ConditionalRowFormatting || []
  );

  const getConditionalRowFormattingInputString = (ruleCount) => {
    if (ruleCount === 1) {
      return "1 Conditional Rule";
    } else if (ruleCount > 1) {
      return `${ruleCount} Conditional Rules`;
    }

    return null;
  };

  let conditionalRowFormattingString = getConditionalRowFormattingInputString(
    conditionalRowFormatting?.length || 0
  );

  useEffect(() => {
    setConditionalRowFormatting(templateData.value?.properties?.ConditionalRowFormatting || []);
    conditionalRowFormattingString = getConditionalRowFormattingInputString(
      conditionalRowFormatting?.length || 0
    );
  }, [templateData.value]);

  const handlePropertyChange = ({ propName, propValue }) => {
    setConditionalRowFormatting(propValue);
    templateData.value = {
      ...templateData.value,
      properties: {
        ...templateData.value.properties,
        [propName]: propValue,
      },
    };
  };
  //End Conditional Formatting

  const [newItemText, setNewItemText] = useState("");
  const [newTagText, setNewTagText] = useState("");

  const handleAddTag = () => {
    if (newTagText.trim() !== "") {
      const currentTags = templateData.value.properties.Tags || [];
      if (!currentTags.includes(newTagText.trim())) {
        templateData.value = {
          ...templateData.value,
          properties: {
            ...templateData.value.properties,
            Tags: [...currentTags, newTagText.trim()],
          },
        };
      }
      setNewTagText("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const currentTags = templateData.value.properties.Tags || [];
    templateData.value = {
      ...templateData.value,
      properties: {
        ...templateData.value.properties,
        Tags: currentTags.filter((tag) => tag !== tagToRemove),
      },
    };
  };

  const onDragOverStageItem = (event) => {
    templateData.value = {
      ...templateData.value,
      properties: { ...templateData.value.properties, StageSteps: event.newState },
    };
  };

  const addStageItem = () => {
    if (newItemText.trim() !== "") {
      const newItem = {
        id: stageStepsMaxID.value + 1,
        text: newItemText.trim(),
      };

      const updatedStageSteps = templateData.value.properties?.StageSteps
        ? [...templateData.value.properties.StageSteps, newItem]
        : [newItem];

      templateData.value = {
        ...templateData.value,
        properties: {
          ...templateData.value.properties,
          StageSteps: updatedStageSteps,
        },
      };

      setNewItemText("");
    }
  };

  const SEPARATOR = "_";

  const dragClueTreeView = useRef();
  const dragOverCntTreeView = useRef(0);
  const isDragDropTreeView = useRef(false);

  function getSiblings(itemIndex, data) {
    let result = data;
    const indices = itemIndex.split(SEPARATOR).map((index) => Number(index));
    for (let i = 0; i < indices.length - 1; i++) {
      result = result[indices[i]].items || [];
    }
    return result;
  }

  const getClueClassName = (event) => {
    const eventAnalyzer = new TreeViewDragAnalyzer(event).init();
    const { itemHierarchicalIndex: itemIndex } = eventAnalyzer.destinationMeta;
    if (eventAnalyzer.isDropAllowed) {
      switch (eventAnalyzer.getDropOperation()) {
        case "before":
          return itemIndex === "0" || itemIndex.endsWith(`${SEPARATOR}0`)
            ? "k-i-insert-up"
            : "k-i-insert-middle";
        case "after": {
          const siblings = getSiblings(itemIndex, templateTreeView.value);
          const lastIndex = Number(itemIndex.split(SEPARATOR).pop());
          return lastIndex < siblings.length - 1 ? "k-i-insert-middle" : "k-i-insert-down";
        }
        default:
          break;
      }
    }
    return "k-i-cancel";
  };

  const onItemDragOver = (event) => {
    dragOverCntTreeView.current++;
    dragClueTreeView.current.show(
      event.pageY - 150,
      event.pageX - 50,
      event.item.text,
      getClueClassName(event)
    );
  };

  const onItemDragEnd = (event) => {
    isDragDropTreeView.current = dragOverCntTreeView.current > 0;
    dragOverCntTreeView.current = 0;
    dragClueTreeView.current.hide();
    const eventAnalyzer = new TreeViewDragAnalyzer(event).init();
    if (eventAnalyzer.isDropAllowed) {
      const dropOperation = eventAnalyzer.getDropOperation();
      if (dropOperation === "before" || dropOperation === "after") {
        const updatedTree = moveTreeViewItem(
          event.itemHierarchicalIndex,
          templateTreeView.value,
          dropOperation,
          eventAnalyzer.destinationMeta.itemHierarchicalIndex
        );

        const updatedTreeWithFields = updatedTree.map((section) => ({
          sectionId: section.id,
          sectionName: section.text,
          properties: section.properties,
          fields: section.items?.map((field) => {
            const originalField = templateData.value.sections
              .find((sec) => sec.fields.some((f) => f.fieldName === field.text))
              ?.fields.find((f) => f.fieldName === field.text);

            return {
              fieldId: field.id,
              fieldName: field.text,
              fieldType: field.type,
              properties: originalField?.properties || {},
            };
          }),
        }));

        templateData.value = { ...templateData.value, sections: updatedTreeWithFields };
      }
    }
    isDragging.value = false;
  };

  const handleMouseEnter = () => {
    isHovering.value = true;
  };

  const handleMouseLeave = () => {
    isHovering.value = false;
  };

  const onHierarchyExpandChange = (event) => {
    expandedHierarchyItems.value = {
      ...expandedHierarchyItems.value,
      [event.item.text]: !expandedHierarchyItems.value[event.item.text],
    };
  };

  const handleContextMenu = (e) => {
    selectedTreeItem.value = e.item;

    e.nativeEvent.preventDefault();
    showContextMenu.value = true;
    contextMenuPosition.value = { left: e.syntheticEvent.clientX, top: e.syntheticEvent.clientY };
  };

  const handleMenuItemClick = () => {
    let tempTreeView = templateTreeView.value.slice();

    if (selectedTreeItem.value.type === "section") {
      const baseText = selectedTreeItem.value.text;
      const copyText = `${baseText} - Copy`;
      let copyNumber = 1;

      tempTreeView.forEach((section) => {
        if (section.text.startsWith(copyText)) {
          const match = section.text.match(/\((\d+)\)$/);
          if (match) {
            copyNumber = Math.max(copyNumber, parseInt(match[1], 10) + 1);
          } else {
            copyNumber = Math.max(copyNumber, 2);
          }
        }
      });

      const newText =
        copyNumber === 1 ? `${baseText} - Copy` : `${baseText} - Copy (${copyNumber})`;
      console.log(selectedTreeItem);
      const copiedItems = selectedTreeItem.value.items.map((item) => {
        const itemBaseText = item.text.replace(/ - Copy(\s\(\d+\))?$/, "");
        const itemCopyText = `${itemBaseText} - Copy`;
        let itemCopyNumber = 1;

        tempTreeView.forEach((section) => {
          section.items.forEach((it) => {
            if (it.text.startsWith(itemCopyText)) {
              const match = it.text.match(/\((\d+)\)$/);
              if (match) {
                itemCopyNumber = Math.max(itemCopyNumber, parseInt(match[1], 10) + 1);
              } else {
                itemCopyNumber = Math.max(itemCopyNumber, 2);
              }
            }
          });
        });

        const newItemText =
          itemCopyNumber === 1
            ? `${itemBaseText} - Copy`
            : `${itemBaseText} - Copy (${itemCopyNumber})`;
        return {
          ...item,
          text: newItemText,
        };
      });

      const selectedSectionCopy = {
        ...selectedTreeItem.value,
        text: newText,
        items: copiedItems,
      };

      const index = tempTreeView.findIndex(
        (section) => section.text === selectedTreeItem.value.text
      );
      if (index !== -1) {
        tempTreeView.splice(index + 1, 0, selectedSectionCopy);
      }
    } else {
      const baseText = selectedTreeItem.value.text;
      const copyText = `${baseText} - Copy`;
      let copyNumber = 1;

      const section = tempTreeView.find((section) =>
        section.items.some((item) => item.text === selectedTreeItem.value.text)
      );

      if (section) {
        tempTreeView.forEach((section) => {
          section.items.forEach((item) => {
            if (item.text.startsWith(copyText)) {
              const match = item.text.match(/\((\d+)\)$/);
              if (match) {
                copyNumber = Math.max(copyNumber, parseInt(match[1], 10) + 1);
              } else {
                copyNumber = Math.max(copyNumber, 2);
              }
            }
          });
        });

        const newText =
          copyNumber === 1 ? `${baseText} - Copy` : `${baseText} - Copy (${copyNumber})`;
        const selectedTreeItemCopy = {
          ...selectedTreeItem.value,
          text: newText,
        };

        const foundItem = section.items.find((item) => item.text === selectedTreeItem.value.text);
        if (foundItem) {
          const index = section.items.indexOf(foundItem);
          section.items.splice(index + 1, 0, selectedTreeItemCopy);
        }
      }
    }

    const nameCounts = {};
    tempTreeView.forEach((section) => {
      section.items.forEach((item, index) => {
        let itemName = item.text;
        let baseName = itemName.replace(/ - Copy(\s\(\d+\))?$/, "");
        if (!nameCounts[baseName]) {
          nameCounts[baseName] = 0;
        }
        nameCounts[baseName]++;
        if (nameCounts[baseName] > 1) {
          itemName = `${baseName} - Copy${
            nameCounts[baseName] > 2 ? ` (${nameCounts[baseName] - 1})` : ""
          }`;
        }
        section.items[index] = { ...item, text: itemName };
      });
    });

    const tempTreeWithFields = tempTreeView.map((section) => ({
      sectionId: section.id,
      sectionName: section.text,
      properties: JSON.parse(JSON.stringify(section.properties || {})),
      fields: section.items.map((field) => {
        return {
          fieldId: field.id,
          fieldName: field.text,
          fieldType: field.type,
          properties: JSON.parse(JSON.stringify(field.properties || {})),
        };
      }),
    }));

    templateData.value = { ...templateData.value, sections: tempTreeWithFields };
  };

  const handleAllowFieldNameEditChange = (e) => {
    if (e.value) {
      showConfirmFieldNameEditDialog.value = true;
    } else {
      templateData.value = {
        ...templateData.value,
        properties: { ...templateData.value.properties, AllowFieldNameEdit: false },
      };
    }
  };

  const handleCancelAllowFieldNameEditDialog = () => {
    showConfirmFieldNameEditDialog.value = false;
    templateData.value = {
      ...templateData.value,
      properties: { ...templateData.value.properties, AllowFieldNameEdit: false },
    };
  };

  const handleConfirmAllowFieldNameEditDialog = () => {
    showConfirmFieldNameEditDialog.value = false;
    templateData.value = {
      ...templateData.value,
      properties: { ...templateData.value.properties, AllowFieldNameEdit: true },
    };
  };

  const handleUnsavedCheckChange = (e) => {
    templateData.value = {
      ...templateData.value,
      properties: { ...templateData.value.properties, UnsavedChangesAlert: e.target.value },
    };
  };

  const handleAutomationSwitchChange = (e) => {
    templateData.value = {
      ...templateData.value,
      properties: { ...templateData.value.properties, DisableTemplateAutomations: e.target.value },
    };
  };

  const handleBlockedStagesChange = (e) => {
    templateData.value = {
      ...templateData.value,
      properties: { ...templateData.value.properties, BlockedStagesOnNewEntry: e.target.value },
    };
  };

  const getConnectedFields = () => {
    const childTemplate = userTemplates.value.find(
      (template) => template.id === templateData.value.properties.ChildTemplate.id
    );

    if (!childTemplate) return [];

    // Get all field names from the current template
    const currentTemplateFieldNames = templateData.value.sections.flatMap((section) =>
      section.fields.map((field) => field.fieldName)
    );

    // Find all fields in childTemplate that have matching fieldNames
    const connectedFields = childTemplate.sections.flatMap((section) =>
      section.fields.filter((field) => currentTemplateFieldNames.includes(field.fieldName))
    );

    return connectedFields;
  };

  return (
    <div className="k-d-flex k-flex-column !k-h-full">
      <div className="k-d-flex k-justify-content-between k-mt-10px k-pr-1 k-pl-1">
        {templateInformationPaneVisible.value && PaneHeader}
        <div className="k-vertical-align-center !k-ml-2">
          <ColorChangingIcon
            name="btnCloseTemplateInformationPane"
            title="Close"
            icon={templateInformationPaneVisible.value ? faChevronLeft : faChevronRight}
            initialPrimaryColor="#a6e6ff"
            initialSecondaryColor="#0078d4"
            initialSize={32}
            hoverSize={32}
            onClick={() => {
              batch(() => {
                templateInformationPaneVisible.value = !templateInformationPaneVisible.value;
              });
            }}
          />
        </div>
      </div>
      {templateInformationPaneVisible.value && (
        <>
          <div className="k-p-1 k-mt-10px">
            <TextBox
              name="txtTemplateInformationSearch"
              placeholder="Search..."
              onChange={(e) => {
                templateInformationSearch.value = e.target.value;
              }}
              suffix={() => ClearInputSuffix()}
              value={templateInformationSearch.value}
            />
          </div>
          <div className="separator" />
          {selectedTemplateMenuItem.value === "Template Properties" && (
            <div className="k-p-1">
              <div className="k-mb-2">
                <Label>Child Template</Label>
                <DropDownList
                  data={userTemplates.value.map((template) => ({
                    id: template.id,
                    name: template.templateName,
                  }))}
                  textField="name"
                  valueField="id"
                  onChange={(e) => {
                    templateData.value = {
                      ...templateData.value,
                      properties: {
                        ...templateData.value.properties,
                        ChildTemplate: e.target.value,
                      },
                    };
                  }}
                  value={templateData.value.properties.ChildTemplate}
                  defaultItem={{ id: "", name: "None" }}
                />
                {templateData.value.properties.ChildTemplate && (
                  <div className="k-mb-2">
                    <Label>Connected Field</Label>
                    <DropDownList
                      data={getConnectedFields()}
                      textField="fieldName"
                      value={templateData.value.properties.ConnectedField}
                      onChange={(e) => {
                        templateData.value = {
                          ...templateData.value,
                          properties: {
                            ...templateData.value.properties,
                            ConnectedField: e.target.value,
                          },
                        };
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="k-mb-2">
                <Checkbox
                  value={templateData.value.properties.AllowFieldNameEdit}
                  label="Allow field name edit on SQL Sync mode"
                  onChange={(e) => {
                    handleAllowFieldNameEditChange(e);
                  }}
                ></Checkbox>
                {templateData.value.properties.AllowFieldNameEdit && (
                  <div className="!k-d-flex k-font-size-sm !k-font-weight-semibold !k-text-error">
                    Updating the field name will result in data loss in SQL and Power BI.
                  </div>
                )}
              </div>
              <div className="k-mb-2">
                <Checkbox
                  defaultValue={templateData.value.properties.UnsavedChangesAlert}
                  label="Show unsaved changes alert"
                  onChange={(e) => {
                    handleUnsavedCheckChange(e);
                  }}
                ></Checkbox>
              </div>
              <div className="k-mb-2">
                <Switch
                  checked={templateData.value?.properties?.DisableTemplateAutomations}
                  name="DisableTemplateAutomations"
                  offLabel="I"
                  onLabel="O"
                  onChange={(e) => {
                    handleAutomationSwitchChange(e);
                  }}
                ></Switch>
                <Label style={{ marginLeft: "5px" }}>
                  <strong>Disable Template Automations</strong>
                </Label>
              </div>
              View Entry Rows Formatting:
              <div className="k-mb-2">
                <TextBox
                  name={"Expression"}
                  label={"Expression"}
                  placeholder="No conditional row formatting rules"
                  readOnly={true}
                  value={conditionalRowFormattingString}
                  prefix={() => expressionPrefix()}
                  suffix={() => expressionSuffix(handlePropertyChange)}
                />
              </div>
              Template Stages:
              <div style={{ display: "flex", gap: "5px" }}>
                <TextBox
                  value={newItemText}
                  onChange={(e) => setNewItemText(e.target.value)}
                  placeholder="Add an item"
                  prefix={() => stagesPrefix(addStageItem)}
                />
                <Button
                  onClick={() => {
                    visibilityBuilderVisibility.value = true;
                    isStageEditor.value = true;
                  }}
                >
                  <BadgeContainer>
                    <ColorChangingIcon
                      icon={faEye}
                      initialPrimaryColor="#605e5c"
                      initialSecondaryColor="#0078D4"
                      initialSize={18}
                      hoverSize={18}
                      margin="1px 5px 0 5px"
                    />
                    <Badge themeColor="warning">
                      {templateData.value?.properties?.VisibilityRules?.length || 0}
                    </Badge>
                  </BadgeContainer>
                </Button>
              </div>
              <Sortable
                className="k-mt-1"
                idField={"id"}
                data={templateData.value.properties.StageSteps ?? []}
                itemUI={SortableItemUI}
                emptyItemUI={() => {}}
                onDragOver={onDragOverStageItem}
              />
              <div className="!k-w-full k-mt-2">
                Blocked Stages on New Entry:
                <MultiSelect
                  required={false}
                  component={MultiSelect}
                  data={templateData.value.properties.StageSteps ?? []}
                  value={templateData.value.properties.BlockedStagesOnNewEntry}
                  onChange={(e) => {
                    handleBlockedStagesChange(e);
                  }}
                  textField="text"
                  dataItemKey="id"
                  placeholder="Select..."
                />
              </div>
              <div className="!k-w-full k-mt-2">
                <Label>Tags</Label>
                <div style={{ display: "flex", gap: "5px" }}>
                  <TextBox
                    value={newTagText}
                    onChange={(e) => setNewTagText(e.target.value)}
                    placeholder="Add a tag"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleAddTag();
                      }
                    }}
                  />
                  <Button
                    themeColor="primary"
                    onClick={handleAddTag}
                    disabled={!newTagText.trim()}
                  >
                    Add Tag
                  </Button>
                </div>
                {(templateData.value.properties.Tags || []).length > 0 && (
                  <div
                    className="k-mt-2 k-d-flex k-flex-wrap"
                    style={{ gap: "8px" }}
                  >
                    {(templateData.value.properties.Tags || []).map((tag, index) => (
                      <Chip
                        key={`${tag}_${index}`}
                        text={tag}
                        removable={true}
                        onRemove={() => handleRemoveTag(tag)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {selectedTemplateMenuItem.value === "Tree View" && (
            <div
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className="k-overflow-y-auto"
            >
              <TreeView
                style={{ cursor: treeViewCursor.value }}
                draggable={true}
                onItemDragStart={() => (isDragging.value = true)}
                onItemDragOver={onItemDragOver}
                onItemDragEnd={onItemDragEnd}
                data={templateTreeView.value}
                expandIcons={true}
                onExpandChange={onHierarchyExpandChange}
                item={(e) => templateHierarchyItem({ e })}
                onContextMenu={(e) => handleContextMenu(e)}
              />
              <TreeViewDragClue ref={dragClueTreeView} />
              <Popup
                show={showContextMenu.value}
                offset={{
                  left: contextMenuPosition.value.left,
                  top: contextMenuPosition.value.top,
                }}
              >
                <Menu
                  vertical={true}
                  style={{ display: "inline-block" }}
                  onSelect={() => handleMenuItemClick()}
                >
                  <MenuItem text="Duplicate item"></MenuItem>
                </Menu>
              </Popup>
            </div>
          )}
          {selectedTemplateMenuItem.value === "Data Connections" && (
            <ListView
              className="!k-bg-transparent !k-overflow-y-scroll !k-h-full !k-flex-grow"
              data={filteredDataConnections.value}
              item={(e) => listViewItem({ dataItem: e.dataItem })}
            />
          )}
          {selectedTemplateMenuItem.value === "Permissions" && (
            <div className="!k-bg-transparent !k-overflow-y-scroll !k-h-full !k-flex-grow">
              <div className="k-p-2">
                <h3 className="!k-pt-0 !k-mt-0">Add User Permission:</h3>
                <Label>Select User or Group:</Label>
                <CustomPeoplePicker
                  selectionMode={"single"}
                  selectedPeople={selectedPerson.value ? [selectedPerson.value] : []}
                  enableGroups={true}
                  onChange={(e) => {
                    existingPermissionErrorMessage.value = null;
                    if (e.target.isGroupSelection) {
                      isGroupSelection.value = true;
                      selectedPeopleFromGroup.value = e.target.selectedPeople || [];
                      selectedPerson.value = e.target.selectedGroup;
                      selectedGroupData.value = e.target.selectedGroup;
                    } else {
                      isGroupSelection.value = false;
                      selectedPeopleFromGroup.value = [];
                      selectedPerson.value = e.target.selectedPeople[0];
                      selectedGroupData.value = null;
                    }
                  }}
                />
                <RadioGroup
                  className="!k-mt-2"
                  value={selectedPermission.value}
                  data={[
                    { label: "Co-Owner", value: "Co-Owner" },
                    { label: "Modify", value: "Modify" },
                    { label: "View", value: "View" },
                    { label: "Make Entry (Modify All)", value: "MakeEntry (Modify All)" },
                    { label: "Make Entry", value: "MakeEntry" },
                    {
                      label: "Make Entry - Restricted View",
                      value: "ViewAuthorOnly",
                    },
                  ]}
                  onChange={(e) => (selectedPermission.value = e.value)}
                />
                <Button
                  type="button"
                  primary="true"
                  onClick={() => {
                    handlePermissionSubmit();
                  }}
                  themeColor="primary"
                  disabled={
                    (!selectedPerson.value && selectedPeopleFromGroup.value.length === 0) ||
                    !selectedPermission.value
                  }
                  hidden={
                    !isAdmin.value &&
                    templateData.value?.owner.toLowerCase() !==
                      userData.value?.userEmail.toLowerCase() &&
                    !templateData.value?.coOwners.some(
                      (co) => co.user.toLowerCase() === userData.value?.userEmail.toLowerCase()
                    )
                  }
                >
                  Add
                </Button>
                {existingPermissionErrorMessage.value && (
                  <div style={{ color: "red", marginTop: "4px", fontSize: "0.875rem" }}>
                    {existingPermissionErrorMessage.value}
                  </div>
                )}
              </div>
              <div className="separator" />
              <TreeView
                data={templatePermissionTreeView.value}
                expandIcons={true}
                item={(e) => templatePermissionItem({ e })}
                onExpandChange={onSharedPermissionExpandChange}
              />
            </div>
          )}
        </>
      )}
      <Popover
        show={showConfigureDataPopover.value}
        position={"bottom"}
        offset={configureDataOffset.value}
        callout={false}
      >
        <div className="align-rows">
          <Button
            fillMode={"flat"}
            onClick={() => {
              templateData.value = {
                ...templateData.value,
                properties: {
                  ...templateData.value.properties,
                  DataConnections: templateData.value.properties.DataConnections.filter(
                    (connection) => {
                      return connection.id !== selectedDataConnectionItem.value.id;
                    }
                  ),
                },
              };
              //loop through sections and field to see if any field has a data property that matches the id of the data connection and remove it
              templateData.value = {
                ...templateData.value,
                sections: templateData.value.sections.map((section) => {
                  return {
                    ...section,
                    fields: section.fields?.map((field) => {
                      if (field.properties?.Data?.id === selectedDataConnectionItem.value.id) {
                        return {
                          ...field,
                          properties: {
                            ...field.properties,
                            Data: null,
                          },
                        };
                      }
                      return field;
                    }),
                  };
                }),
              };
            }}
          >
            Remove
          </Button>
          <Button
            fillMode={"flat"}
            onClick={() => {
              showDataConnectionConfigurator.value = true;
            }}
          >
            Configure
          </Button>
        </div>
      </Popover>
      {showDataConnectionConfigurator.value && (
        <Window
          style={{ zIndex: 1000000000 }}
          key="field-constructor-window"
          title={`Configure connection to ${selectedDataConnectionItem.value.text}`}
          onClose={() => (showDataConnectionConfigurator.value = false)}
          initialHeight={600}
          initialWidth={1500}
          draggable={false}
          modal="false"
        >
          <DataConnectionConfigurator />
        </Window>
      )}
      {showConfirmFieldNameEditDialog.value && (
        <ConfirmAllowFieldNameEditDialog
          handleCancel={handleCancelAllowFieldNameEditDialog}
          handleConfirm={handleConfirmAllowFieldNameEditDialog}
        />
      )}
    </div>
  );
};

const ClearInputSuffix = () => {
  return (
    <>
      <InputSeparator
        style={{
          width: "1px",
          backgroundColor: "gainsboro",
          margin: "4px 0px",
        }}
      />
      <InputClearValue onClick={() => (templateInformationSearch.value = "")}>
        <Icon name="x" />
      </InputClearValue>
    </>
  );
};
